/**
 * Commentary handler — on-demand ML commentary for scripture-first messages.
 *
 * One step: requestCommentary marks the source 'loaded' (its Generate button
 * hides) and inserts a NEW assistant bubble after it into which the streamed
 * commentary lands, keeping the visual order:
 *   verse bubble → commentary bubble.
 *
 * A "step back and read the pericope" bubble used to gate this on a second
 * click. V6.4 commentary interweaves the passage itself — every block opens
 * with its verses — so the gate asked for a read the answer already gives
 * (removed 2026-09-21).
 */

import { runInAction } from 'mobx';
import type { ChatEvent } from '@skjv/common';
import { fetchCommentaryStreaming, QueueFullError } from '@api/chat';
import type { ChatMessage } from './types';
import { commentaryHistory } from './commentary-history';
import { theaterEventPatch, waitForTheaterDone } from './theater-events';

/** Start commentary for `messageId`. `readerThoughts` (experimental) is
 *  captured HERE, at the moment the user pressed Generate — it parks on the
 *  source message so the fetch reads one ground truth. */
export async function requestCommentary(
  messages: ChatMessage[],
  persistLocal: () => void,
  nextId: () => string,
  messageId: string,
  readerThoughts?: string,
): Promise<void> {
  const sourceIdx = messages.findIndex((m) => m.id === messageId);
  if (sourceIdx === -1) return;
  const sourceMsg = messages[sourceIdx];
  if (!sourceMsg?.scriptureResponse || !sourceMsg.originalQuery) return;

  const trimmedThoughts = readerThoughts?.trim() || undefined;
  runInAction(() => {
    messages[sourceIdx] = {
      ...messages[sourceIdx],
      ...(trimmedThoughts ? { readerThoughts: trimmedThoughts } : {}),
      // 'loaded' keeps the Generate button hidden for the whole run — the
      // progress lives in the commentary bubble below, never in two places.
      commentaryState: 'loaded',
    };
    persistLocal();
  });

  return startStandaloneCommentary(messages, persistLocal, nextId, messageId, sourceIdx + 1);
}

/** Run a failed commentary again: drop the error bubble and re-request
 *  from the scripture message it belonged to. */
export async function retryCommentary(
  messages: ChatMessage[],
  persistLocal: () => void,
  nextId: () => string,
  failedMessageId: string,
): Promise<void> {
  const failedIdx = messages.findIndex((m) => m.id === failedMessageId);
  if (failedIdx === -1) return;
  const sourceMessageId = messages[failedIdx].commentaryForMessageId;
  if (!sourceMessageId) return;

  runInAction(() => {
    messages.splice(failedIdx, 1);
    persistLocal();
  });
  return requestCommentary(messages, persistLocal, nextId, sourceMessageId);
}

/** Insert a new assistant bubble at `insertAt` and stream commentary into it. */
async function startStandaloneCommentary(
  messages: ChatMessage[],
  persistLocal: () => void,
  nextId: () => string,
  sourceMessageId: string,
  insertAt: number,
): Promise<void> {
  const sourceIdx = messages.findIndex((m) => m.id === sourceMessageId);
  if (sourceIdx === -1) return;
  const sourceMsg = messages[sourceIdx];
  if (!sourceMsg?.scriptureResponse || !sourceMsg.originalQuery) return;

  const commentaryMsgId = nextId();
  runInAction(() => {
    messages.splice(insertAt, 0, {
      id: commentaryMsgId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
      isLoading: true,
      progressSteps: [],
      commentaryForMessageId: sourceMessageId,
    });
    persistLocal();
  });

  const findTarget = () => messages.findIndex((m) => m.id === commentaryMsgId);
  // The reader's clock, started at the click: it counts the queue wait and
  // the network the server's own duration cannot see.
  const startedAt = performance.now();

  const handleProgress = (event: ChatEvent) => {
    runInAction(() => {
      const targetIdx = findTarget();
      if (targetIdx === -1) return;
      const current = messages[targetIdx];
      const theaterPatch = theaterEventPatch(current, event);
      if (theaterPatch) {
        messages[targetIdx] = { ...current, ...theaterPatch };
      } else if (event.type === 'queue') {
        // 'full' never reaches here — the transport throws QueueFullError.
        messages[targetIdx] = {
          ...current,
          queueStatus: event.state === 'waiting'
            ? {
                state: 'waiting',
                position: event.position,
                depth: event.depth,
                estimatedWaitMs: event.estimatedWaitMs,
                receivedAt: Date.now(),
              }
            : undefined,
        };
      } else if (event.type === 'step') {
        const steps = [...(current.progressSteps ?? [])];
        if (event.status === 'running') {
          steps.push({ step: event.step, status: 'running' });
        } else {
          const s = steps.find((s) => s.step === event.step);
          if (s) { s.status = 'done'; s.durationMs = event.durationMs; }
        }
        messages[targetIdx] = { ...current, progressSteps: steps };
      } else if (event.type === 'inference-progress') {
        messages[targetIdx] = {
          ...current,
          inferenceProgress: { tokenCount: event.tokenCount, elapsedMs: event.elapsedMs },
        };
      } else if (event.type === 'inference-meta') {
        messages[targetIdx] = {
          ...current,
          inferenceMeta: {
            hostname: event.hardware.hostname,
            cpuModel: event.hardware.cpuModel,
            cpuCores: event.hardware.cpuCores,
            totalMemoryGb: event.hardware.totalMemoryGb,
            freeMemoryGb: event.hardware.freeMemoryGb,
            platform: event.hardware.platform,
            arch: event.hardware.arch,
            ollamaHost: event.hardware.ollamaHost,
            model: event.model,
            maxTokens: event.maxTokens,
          },
        };
      }
    });
  };

  try {
    const verseRefs = sourceMsg.scriptureResponse.verses.map((v) => v.reference);
    // Pericope-scoped studies request whole-passage commentary; the anchor
    // (verses[0]) is already the pericope's first verse.
    const mode = sourceMsg.scriptureResponse.scope === 'pericope' ? 'pericope' : undefined;
    const result = await fetchCommentaryStreaming(
      verseRefs, sourceMsg.originalQuery, handleProgress,
      { mode, readerThoughts: sourceMsg.readerThoughts },
    );

    // Let the edit theater finish its performance before the typeset —
    // the ground truth below replaces the draft either way.
    await waitForTheaterDone(messages, commentaryMsgId);

    runInAction(() => {
      const targetIdx = findTarget();
      if (targetIdx === -1) return;
      const sr = sourceMsg.scriptureResponse;
      const variantSummary = sr?.variants[0]
        ? `${sr.variants[0].affectedConcepts.join(', ') || sr.variants[0].category} (impact: ${sr.variants[0].theologicalImpact})`
        : undefined;
      const crossRefTexts: Record<string, string> = {};
      for (const cr of sr?.crossRefs ?? []) {
        if (cr.toText) crossRefTexts[cr.to] = cr.toText;
      }
      // Route the result through `commentary` (not `content`) so RichMessageContent
      // renders it inside CommentarySection and lights up the bottom Report button.
      // Propagate the deterministic context (verse text, pericope, cross-refs,
      // variants) so the Report dialog can populate every section.
      messages[targetIdx] = {
        ...messages[targetIdx],
        content: '',
        commentary: result.response,
        rawModelBody: result.rawModelBody,
        prompt: result.prompt,
        // Carry the source intent so pericope commentary bubbles are
        // findable (e.g. by the Add to Study CTA).
        intent: sourceMsg.intent,
        reference: sr?.verses[0]?.reference,
        itIsWritten: sr?.verses.map((v) => v.text).join(' '),
        pericope: sr?.pericope ?? null,
        // V6.4: verse-anchor labels render the range's actual KJV — carry
        // the per-verse text since this bubble has no scriptureResponse.
        // The commentary payload is authoritative (the server-expanded
        // range the model expounded); the scripture response's verses are
        // the fallback (pericope studies hold only the anchor verse there).
        passageVerses:
          result.passageVerses ??
          sr?.verses.map((v) => ({ verse: v.verse, text: v.text })),
        crossReferences: sr?.crossRefs.map((cr) => cr.to) ?? [],
        crossRefTexts: Object.keys(crossRefTexts).length > 0 ? crossRefTexts : undefined,
        variantSummary,
        // The reader's seed thoughts travel with the commentary they shaped —
        // Add to Study and the history browser both read them from here.
        readerThoughts: sourceMsg.readerThoughts,
        originalQuery: sourceMsg.originalQuery,
        reportMeta: {
          run: result.runMeta,
          clientElapsedMs: Math.round(performance.now() - startedAt),
        },
        isLoading: false,
        // The verification receipt persists onto the final message.
        editReceipt: result.editReceipt ?? messages[targetIdx].editReceipt,
        // Ground-truth reconciliation: the animated draft is done — the
        // rendered body comes from `commentary` above, never the buffer.
        // `editChecks` stays: the accuracy checklist persists on the final
        // message as a collapsed accordion the reader can reopen.
        streamingText: undefined,
        editBaseline: undefined,
        editOps: undefined,
        theaterDone: undefined,
        progressSteps: undefined,
        inferenceProgress: undefined,
        inferenceMeta: undefined,
        queueStatus: undefined,
      };
      persistLocal();
      // Every finished commentary joins the browsable history (last 12).
      commentaryHistory.record({
        pericopeTitle: sr?.pericope?.title ?? null,
        reference: sr?.verses[0]?.reference ?? '',
        text: result.response,
        rawModelBody: result.rawModelBody,
        itIsWritten: sr?.verses.map((v) => v.text).join(' '),
        pericope: sr?.pericope ?? null,
        intent: sourceMsg.intent,
        generatedAt: new Date().toISOString(),
        passageVerses:
          result.passageVerses ??
          sr?.verses.map((v) => ({ verse: v.verse, text: v.text })),
        readerThoughts: sourceMsg.readerThoughts,
        reportMeta: {
          run: result.runMeta,
          clientElapsedMs: Math.round(performance.now() - startedAt),
        },
      });
    });
  } catch (err) {
    if (err instanceof QueueFullError) {
      // A kind turn-away, not a failure: render the friendly copy without
      // error styling and restore the Generate button for a later retry.
      runInAction(() => {
        const targetIdx = findTarget();
        if (targetIdx !== -1) {
          messages[targetIdx] = {
            ...messages[targetIdx],
            content: err.message,
            isLoading: false,
            streamingText: undefined,
            progressSteps: undefined,
            queueStatus: undefined,
          };
        }
        const srcIdx = messages.findIndex((m) => m.id === sourceMessageId);
        if (srcIdx !== -1) {
          messages[srcIdx] = { ...messages[srcIdx], commentaryState: 'not-requested' };
        }
        persistLocal();
      });
      return;
    }
    const errorMsg = err instanceof Error ? err.message : 'Unknown error';
    const isQc = errorMsg.includes('QC') || errorMsg.includes('quality');
    runInAction(() => {
      const targetIdx = findTarget();
      if (targetIdx === -1) return;
      messages[targetIdx] = {
        ...messages[targetIdx],
        content: isQc
          ? 'Commentary could not be generated — it did not pass quality control.'
          : 'Commentary could not be generated at this time. The AI server may be unavailable.',
        isLoading: false,
        isError: true,
        errorDetail: errorMsg,
        streamingText: undefined,
        progressSteps: undefined,
        inferenceProgress: undefined,
        inferenceMeta: undefined,
        queueStatus: undefined,
      };
      persistLocal();
    });
  }
}
